// Place Value Tower generator. Build a number digit by digit into place columns, then at
// the higher rungs answer a rounding or expanded-form question about it.

import { int, pick, shuffle, type Rng } from '@kit/rng';

export type PlaceName = 'thousands' | 'hundreds' | 'tens' | 'ones' | 'tenths' | 'hundredths';

export interface Column { place: PlaceName; digit: number }

export interface PVProblem {
  kind: 'build' | 'round' | 'expanded';
  /** columns high to low */
  columns: Column[];
  /** display form, e.g. "4,007" or "3.07" */
  display: string;
  text: string;
  /** falling order of block digits (indexes into columns) */
  order: number[];
  decimals: number;
  answer?: number;
  choices?: (number | string)[];
}

export const PLACE_LABEL: Record<PlaceName, string> = { thousands: 'thousands', hundreds: 'hundreds', tens: 'tens', ones: 'ones', tenths: 'tenths', hundredths: 'hundredths' };

export function formatNumber(cols: Column[]): string {
  const whole = cols.filter(c => !c.place.endsWith('ths')).map(c => c.digit).join('');
  const frac = cols.filter(c => c.place.endsWith('ths')).map(c => c.digit).join('');
  const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return frac ? `${w}.${frac}` : w;
}

export function valueOf(cols: Column[]): number {
  const mult: Record<PlaceName, number> = { thousands: 1000, hundreds: 100, tens: 10, ones: 1, tenths: 0.1, hundredths: 0.01 };
  return Math.round(cols.reduce((a, c) => a + c.digit * mult[c.place], 0) * 100) / 100;
}

function wholeColumns(digits: number): Column[] {
  const places: PlaceName[] = ['thousands', 'hundreds', 'tens', 'ones'];
  return places.slice(4 - digits).map(p => ({ place: p, digit: 0 }));
}

export function makePlaceValue(level: number, rng: Rng): PVProblem {
  const L = Math.max(1, Math.min(10, level));
  let cols: Column[];
  let decimals = 0;
  if (L <= 2) { cols = wholeColumns(2); cols[0].digit = int(rng, 1, 9); cols[1].digit = int(rng, 0, 9); }
  else if (L <= 4) { cols = wholeColumns(3); cols[0].digit = int(rng, 1, 9); cols[1].digit = int(rng, 0, 9); cols[2].digit = int(rng, 0, 9); }
  else if (L <= 7) {
    cols = wholeColumns(4); cols[0].digit = int(rng, 1, 9);
    cols[1].digit = rng() < 0.5 ? 0 : int(rng, 1, 9);
    cols[2].digit = cols[1].digit === 0 && rng() < 0.5 ? 0 : int(rng, 0, 9);
    cols[3].digit = int(rng, 1, 9);
  } else {
    decimals = L === 8 ? 1 : 2;
    cols = [{ place: 'ones', digit: int(rng, 1, 9) }, { place: 'tenths', digit: int(rng, 0, 9) }];
    if (decimals === 2) cols.push({ place: 'hundredths', digit: int(rng, 1, 9) });
    if (rng() < 0.4) cols.unshift({ place: 'tens', digit: int(rng, 1, 9) });
  }
  const display = formatNumber(cols);
  const order = shuffle(rng, cols.map((_, i) => i));
  const base: PVProblem = { kind: 'build', columns: cols, display, text: `Build ${display}`, order, decimals };

  // question variants at L7 and L9-10
  const roll = rng();
  if (L === 7 && roll < 0.5) {
    const to = pick(rng, [10, 100, 1000] as const);
    const v = valueOf(cols);
    const answer = Math.round(v / to) * to;
    const choices = shuffle(rng, [...new Set([answer, answer + to, answer - to, Math.floor(v / to) * to === answer ? answer + 2 * to : Math.floor(v / to) * to])].filter(x => x >= 0).slice(0, 4));
    while (choices.length < 4) choices.push(answer + (choices.length + 1) * to);
    return { ...base, kind: 'round', text: `Round ${display} to the nearest ${to === 10 ? 'ten' : to === 100 ? 'hundred' : 'thousand'}`, answer, choices };
  }
  if (L >= 9 && roll < 0.4) {
    const mult: Record<PlaceName, number> = { thousands: 1000, hundreds: 100, tens: 10, ones: 1, tenths: 0.1, hundredths: 0.01 };
    const parts = cols.filter(c => c.digit > 0).map(c => { const v = Math.round(c.digit * mult[c.place] * 100) / 100; return v < 1 ? v.toFixed(c.place === 'tenths' ? 1 : 2) : String(v); });
    const answer = valueOf(cols);
    return { ...base, kind: 'expanded', text: `${parts.join(' + ')} = ?`, answer };
  }
  return base;
}

/** the column a falling digit may land in: any unfilled column whose digit matches */
export function validColumns(p: PVProblem, digit: number, filled: boolean[]): number[] {
  return p.columns.map((c, i) => (!filled[i] && c.digit === digit ? i : -1)).filter(i => i >= 0);
}
